import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { SlidePanel } from '@/shared/components';
import { useEosTodos } from '../hooks/useEosTodos';
import { useTeams } from '@/features/rocks/hooks/useTeams';
import { useOwnerOptions } from '@/features/rocks/hooks/useRocks';
import { TodoKanbanBoard } from '../components/TodoKanbanBoard';
import { TodoListView } from '../components/TodoListView';
import { TodoFilters } from '../components/TodoFilters';
import { TodoDetailPanel } from '../components/TodoDetailPanel';
import { TodoGuide } from '../components/TodoGuide';
import type { EosTodo, EosTodoFilters } from '../types';

type ActiveTab = 'kanban' | 'list' | 'guide';

const TABS: { value: ActiveTab; label: string }[] = [
  { value: 'list', label: 'To-Dos' },
  { value: 'kanban', label: 'Kanban' },
  { value: 'guide', label: 'What is a To-Do?' },
];

function usePersistedState<T>(field: string, initial: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const key = `atek-os-todos-${field}`;
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw != null ? (JSON.parse(raw) as T) : initial;
    } catch { return initial; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
  }, [key, value]);
  return [value, setValue];
}

export function OsTodosPage() {
  const [activeTab, setActiveTab] = usePersistedState<ActiveTab>('tab', 'list');
  const [filters, setFilters] = usePersistedState<EosTodoFilters>('filters', { include_completed: true, include_cancelled: false });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<'view' | 'create'>('view');
  const [panelOpen, setPanelOpen] = useState(false);

  const { data, isLoading } = useEosTodos(filters);
  const todos = data?.todos ?? [];

  const { data: teams = [] } = useTeams();
  const { data: ownersRaw = [] } = useOwnerOptions();

  const ownerOptions = useMemo(
    () => ownersRaw.map((o) => ({
      id: o.id,
      label: o.name || o.email,
    })),
    [ownersRaw]
  );
  const teamOptions = useMemo(
    () => teams.map((t) => ({ id: t.id, label: t.name })),
    [teams]
  );

  const ownerLabel = (id: string) => ownerOptions.find(o => o.id === id)?.label ?? id;
  const teamLabel = (id: string | null) => (id ? teamOptions.find(t => t.id === id)?.label ?? '—' : '—');

  const openTodo = (t: EosTodo) => {
    setSelectedId(t.id);
    setPanelMode('view');
    setPanelOpen(true);
  };

  const openCreate = () => {
    setSelectedId(null);
    setPanelMode('create');
    setPanelOpen(true);
  };

  const handlePanelClose = (open: boolean) => {
    setPanelOpen(open);
    if (!open) { setSelectedId(null); setPanelMode('view'); }
  };

  return (
    <div className="-m-5 flex flex-col h-[calc(100%+40px)] bg-background">
      <div className="flex items-center justify-between gap-4 border-b px-4 h-12 shrink-0">
        <div className="flex items-center gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" /> New To-Do
        </Button>
      </div>

      {activeTab !== 'guide' && (
        <div className="px-4 py-2 border-b shrink-0">
          <TodoFilters filters={filters} onChange={setFilters} ownerOptions={ownerOptions} />
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0 overflow-auto p-4">
          {activeTab === 'kanban' && (
            <TodoKanbanBoard
              todos={todos}
              onSelect={openTodo}
              ownerLabel={ownerLabel}
              teamLabel={teamLabel}
            />
          )}
          {activeTab === 'list' && (
            <TodoListView
              todos={todos}
              isLoading={isLoading}
              onSelect={openTodo}
              ownerLabel={ownerLabel}
              teamLabel={teamLabel}
            />
          )}
          {activeTab === 'guide' && <TodoGuide />}
        </div>

        <SlidePanel open={panelOpen} storageKey="atek_todos_panel_width">
          <TodoDetailPanel
            key={panelMode === 'create' ? 'create' : selectedId}
            todoId={selectedId}
            mode={panelMode}
            open={panelOpen}
            onOpenChange={handlePanelClose}
            onCreated={(id) => { setSelectedId(id); setPanelMode('view'); }}
            ownerOptions={ownerOptions}
            teamOptions={teamOptions}
          />
        </SlidePanel>
      </div>
    </div>
  );
}
